import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Optional
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';

@Directive({
  selector: '[appOverflowTooltip]'
})
export class OverFlowTooltipDirective
  implements AfterViewInit, OnDestroy {

  @Input('appOverflowTooltip') text = '';

  private mutationObserver?: MutationObserver;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    @Optional() private readonly tooltip: MatTooltip
  ) {}

  ngAfterViewInit(): void {
    Promise.resolve().then(() => this.configureTooltip());
    this.observeChanges();
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.configureTooltip();
  }

  @HostListener('input')
  onInput(): void {
    this.configureTooltip();
  }

  @HostListener('blur')
  onBlur(): void {
    this.configureTooltip();
  }

  ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
  }

  private observeChanges(): void {
    const element = this.elementRef.nativeElement;

    this.mutationObserver = new MutationObserver(() => {
      this.configureTooltip();
    });

    this.mutationObserver.observe(element, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      subtree: true
    });
  }

  private configureTooltip(): void {
    if (!this.tooltip) {
      return;
    }

    const element = this.elementRef.nativeElement;

    let hasOverflow = false;
    let message = this.text;

    if (
      element instanceof HTMLInputElement ||
      element instanceof HTMLTextAreaElement
    ) {
      // En campos editables, revisar el texto actual.
      message = element.value || this.text;

      if (element instanceof HTMLTextAreaElement) {
        hasOverflow =
          element.scrollHeight > element.clientHeight ||
          this.textWidth(element) > element.clientWidth;
      } else {
        hasOverflow =
          this.textWidth(element) > element.clientWidth;
      }
    } else {
      // Mantener el comportamiento de los elementos normales.
      const descendants = Array.from(
        element.querySelectorAll<HTMLElement>('*')
      );

      const allElements = [element, ...descendants];

      const hasTextOverflow = allElements.some(
        el =>
          el.scrollWidth > el.clientWidth ||
          el.scrollHeight > el.clientHeight
      );

      const hasHiddenChildren = descendants.some(
        el => getComputedStyle(el).display === 'none'
      );

      hasOverflow = hasTextOverflow || hasHiddenChildren;
    }

    this.tooltip.message = message;
    this.tooltip.disabled = !hasOverflow;
  }

  private textWidth(
    element: HTMLInputElement | HTMLTextAreaElement
  ): number {
    const style = getComputedStyle(element);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      return 0;
    }

    context.font = style.font;

    const text = element.value || this.text;
    const width = context.measureText(text).width;

    const padding =
      parseFloat(style.paddingLeft) +
      parseFloat(style.paddingRight);

    // Considerar el espacio interior del campo.
    return width + padding;
  }
}
